// <pfs-collapsible>: ein einzelnes auf-/zuklappbares Panel (PFS-729). Header
// (klickbar, Titel + Chevron) + Body (Default-Slot). Toggelt UNABHÄNGIG; die
// Accordion-Semantik (nur eins offen) macht <pfs-collapsible-group data-mode>.
//
// Wiederverwendbar außerhalb des Steppers: Terminlisten (Kommende/Vergangene),
// FAQ, Settings-Sektionen. Der Stepper komponiert es (aktive Zeile = offen).
//
// Header: data-title (einfacher Text) ODER <div slot="header"> (beliebiger
// Inhalt, z.B. eine <pfs-checklist>-Zeile). Body: Default-Slot.
//
// Attribute (alle data-*):
//   data-title   Header-Text (ignoriert, wenn slot="header" befüllt ist)
//   data-open    präsent = offen. Reaktiv: der Host/die Gruppe steuert darüber.
//
// Event: pfs:collapsible-toggle (bubbles+composed, detail {open}) — NUR bei
// User-Interaktion (Klick auf den Header), NICHT bei programmatischem data-open
// (sonst Endlosschleife mit der Gruppe).

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { escapeHTML } from "../../utils/escape-html.js";

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; }

.head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 0.9rem;
  font: inherit;
  font-weight: 600;
  text-align: left;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
}
.head:hover { background: var(--pfs-color-surface-hover); }
.head:focus-visible {
  outline: none;
  box-shadow: var(--pfs-shadow-focus-ring-strong, 0 0 0 3px rgba(59, 130, 246, 0.45));
}

.title { flex: 1 1 auto; min-width: 0; }

.chev {
  flex: none;
  color: var(--pfs-color-on-surface-subtle);
  transition: transform 0.2s ease;
}
:host([data-open]) .chev { transform: rotate(90deg); }

.body { display: none; padding: 0 0.9rem 0.85rem; }
:host([data-open]) .body { display: block; }

@media (prefers-reduced-motion: reduce) { .chev { transition: none; } }
`;

const CHEVRON = `<svg class="chev" viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="m9 18 6-6-6-6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

defineComponent({
  tagName: "pfs-collapsible",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;

    const head = document.createElement("button");
    head.className = "head";
    head.type = "button";
    const titleWrap = document.createElement("span");
    titleWrap.className = "title";
    // slot="header" hat Vorrang; data-title ist der einfache Fallback.
    titleWrap.innerHTML = `<slot name="header">${escapeHTML(host.dataset.title ?? "")}</slot>`;
    head.innerHTML = "";
    head.append(titleWrap);
    head.insertAdjacentHTML("beforeend", CHEVRON);

    const body = document.createElement("div");
    body.className = "body";
    body.innerHTML = "<slot></slot>";

    root.append(style, head, body);

    const syncAria = (): void => {
      head.setAttribute("aria-expanded", host.hasAttribute("data-open") ? "true" : "false");
    };
    syncAria();

    // User-Klick: Zustand kippen + Event (der einzige Ort, der emittiert).
    const onClick = (): void => {
      const open = !host.hasAttribute("data-open");
      if (open) host.setAttribute("data-open", "");
      else host.removeAttribute("data-open");
      syncAria();
      host.dispatchEvent(
        new CustomEvent("pfs:collapsible-toggle", { bubbles: true, composed: true, detail: { open } }),
      );
    };
    head.addEventListener("click", onClick);
    ctx.cleanup.push(() => head.removeEventListener("click", onClick));

    // Reaktiv auf data-open (programmatische Steuerung durch die Gruppe) —
    // nur ARIA nachziehen, KEIN Event (Body-Sichtbarkeit macht CSS via :host).
    const obs = new MutationObserver(syncAria);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, { attributes: true, attributeFilter: ["data-open"] });
  },
});
